// Comprehensive Training Module Content for the 10 Skills of School Risk Intelligence

export const TRAINING_MODULES = [
  {
    id: 'skill1',
    title: 'Define Reality',
    description: 'Assess changing conditions, systems, and stakeholder relationships',
    duration: 60,
    icon: 'target',
    color: '#2563EB',
    overview: 'Learn to accurately assess your district\'s current risk landscape by examining changing conditions, testing assumptions, and understanding stakeholder relationships.',
    objectives: [
      'Identify and challenge assumptions that may create blind spots',
      'Map stakeholder relationships and their risk implications',
      'Assess changing conditions that affect your risk profile',
      'Develop a reality-check framework for ongoing use'
    ],
    lessons: [
      {
        id: 'skill1_l1',
        title: 'Understanding Your Current State',
        duration: 12,
        content: `The first step in building risk intelligence is honestly assessing where your district currently stands. This requires looking beyond surface-level metrics to understand the true state of your risk management capabilities.

**Key Questions to Ask:**
• What risks have materialized in the past 3 years that we didn't anticipate?
• Where do we have documented processes vs. informal practices?
• How do staff members actually perceive our safety culture?
• What would a new employee notice about how we handle risk?

**The Reality Gap**
Most organizations have a gap between their perceived and actual risk management maturity. This gap often exists because:
- Leadership receives filtered information
- Success is measured by absence of incidents rather than presence of controls
- Historical practices are assumed to still be effective
- Comparison is made to peer districts rather than best practices

**Exercise: Reality Inventory**
Create a simple inventory of your current practices:
1. List your top 10 risk areas
2. For each, note: documented policy (Y/N), training provided (Y/N), last review date, responsible person
3. Identify gaps where you have exposure but limited controls`,
        keyTakeaways: [
          'Honest self-assessment is the foundation of risk intelligence',
          'The gap between perceived and actual capabilities is often significant',
          'Regular reality checks prevent complacency'
        ]
      },
      {
        id: 'skill1_l2',
        title: 'Stakeholder Mapping',
        duration: 10,
        content: `Every risk in a school district affects and is affected by multiple stakeholders. Understanding these relationships is crucial for effective risk management.

**Primary Stakeholder Groups:**
• **Students** - The core of your mission; their safety is paramount
• **Staff** - Teachers, administrators, support personnel
• **Parents/Guardians** - Key partners in student safety
• **Board of Education** - Governance and policy oversight
• **Community** - Taxpayers, neighbors, local businesses
• **Regulators** - State education department, OSHA, etc.

**Mapping Stakeholder Risk Relationships**
For each major risk category, consider:
1. Who is directly affected if this risk materializes?
2. Who influences whether this risk is controlled?
3. Who needs to be informed about this risk?
4. Whose cooperation is needed to mitigate this risk?

**Example: School Bus Safety**
- Affected: Students, drivers, other motorists
- Influencers: Transportation director, mechanics, training staff
- Informed: Parents, board, insurance carrier
- Cooperation needed: Drivers, students, crossing guards

**Power/Interest Grid**
Plot stakeholders on a grid:
- High Power/High Interest: Manage closely (Board, Superintendent)
- High Power/Low Interest: Keep satisfied (State regulators)
- Low Power/High Interest: Keep informed (Parents, staff)
- Low Power/Low Interest: Monitor (General public)`,
        keyTakeaways: [
          'Every risk has multiple stakeholders with different perspectives',
          'Understanding stakeholder relationships improves risk communication',
          'Stakeholder mapping reveals hidden dependencies and influences'
        ]
      },
      {
        id: 'skill1_l3',
        title: 'Assumption Testing',
        duration: 12,
        content: `Assumptions are the silent killers of effective risk management. We often operate on beliefs that have never been tested or are no longer valid.

**Common Dangerous Assumptions in K-12:**
• "Our staff knows what to do in an emergency"
• "That could never happen here"
• "We've always done it this way and nothing bad has happened"
• "The insurance will cover it"
• "Someone else is handling that"

**The Assumption Audit Process:**
1. **Identify** - List assumptions underlying your key risk controls
2. **Challenge** - Ask "How do we know this is true?"
3. **Test** - Gather evidence to validate or invalidate
4. **Update** - Revise practices based on findings

**Testing Methods:**
• **Drills and exercises** - Do people actually know procedures?
• **Audits and inspections** - Are controls in place and working?
• **Interviews and surveys** - What do staff really think/know?
• **Incident analysis** - What do near-misses reveal?
• **External review** - What do fresh eyes see?

**Case Study: The Unlocked Door**
A district assumed all exterior doors were locked during school hours. An audit revealed 23% of doors were routinely propped open by staff for convenience. The assumption that "doors are locked" was false, creating significant security vulnerability.

**Exercise: Assumption Inventory**
List 5 key assumptions your district operates on regarding safety. For each:
- How was this assumption established?
- When was it last validated?
- What would it take to test it?`,
        keyTakeaways: [
          'Untested assumptions create invisible vulnerabilities',
          'Regular assumption audits should be part of risk management',
          'Evidence-based validation replaces hope-based management'
        ]
      },
      {
        id: 'skill1_l4',
        title: 'Environmental Scanning',
        duration: 14,
        content: `The environment in which schools operate is constantly changing. Effective risk intelligence requires systematic monitoring of these changes.

**Categories of Environmental Change:**

**1. Regulatory Environment**
• New laws and compliance requirements
• Changes in reporting obligations
• Shifts in enforcement priorities
• Court decisions affecting liability

**2. Social Environment**
• Changing community demographics
• Evolving parent expectations
• Social media dynamics
• Mental health trends among students

**3. Technological Environment**
• New educational technologies
• Cyber threat evolution
• Communication platform changes
• Data privacy requirements

**4. Physical Environment**
• Facility aging and condition
• Climate and weather patterns
• Traffic and development changes
• Infrastructure dependencies

**5. Economic Environment**
• Budget pressures and constraints
• Insurance market conditions
• Staffing market challenges
• Vendor/contractor availability

**Building a Scanning System:**
1. Assign responsibility for monitoring each category
2. Establish information sources (professional associations, legal updates, news)
3. Create a regular reporting cadence
4. Connect environmental changes to risk implications
5. Update risk assessments based on findings

**Early Warning Indicators:**
Develop specific indicators to watch:
• Student discipline referrals trending up
• Staff turnover in key positions
• Maintenance work orders backlog growing
• Parent complaints increasing
• Near-miss incidents occurring`,
        keyTakeaways: [
          'The risk environment is constantly evolving',
          'Systematic scanning prevents being blindsided by change',
          'Early warning indicators enable proactive response'
        ]
      },
      {
        id: 'skill1_l5',
        title: 'Reality Check Framework',
        duration: 12,
        content: `Bringing it all together: create a systematic framework for ongoing reality assessment that becomes part of your district's operating rhythm.

**The Quarterly Reality Check:**

**Week 1: Data Gathering**
• Collect incident and near-miss reports
• Review compliance audit findings
• Gather stakeholder feedback
• Scan environmental changes

**Week 2: Analysis**
• Compare actual vs. expected performance
• Identify emerging patterns or trends
• Test key assumptions
• Assess stakeholder sentiment

**Week 3: Synthesis**
• Update risk register as needed
• Identify priority areas for attention
• Develop action recommendations
• Prepare leadership briefing

**Week 4: Communication**
• Brief leadership team
• Update relevant stakeholders
• Document findings
• Plan next quarter's focus

**Reality Check Questions:**
Ask these questions regularly:
1. What surprised us this quarter?
2. What assumptions proved wrong?
3. What risks are we not talking about?
4. Where is our confidence potentially misplaced?
5. What would an outsider notice that we've become blind to?

**Creating Psychological Safety:**
Reality checks only work if people feel safe sharing bad news:
• Reward early identification of problems
• Separate accountability for reporting from accountability for causing
• Model vulnerability by admitting leadership blind spots
• Follow up positively on concerns raised

**Documentation:**
Maintain a Reality Check Log:
• Date and participants
• Key findings
• Assumptions tested
• Actions taken
• Follow-up items`,
        keyTakeaways: [
          'Systematic reality checking should be a regular practice',
          'Psychological safety enables honest assessment',
          'Documentation creates accountability and tracks progress'
        ]
      }
    ]
  },
  {
    id: 'skill2',
    title: 'Fine Tune Systems',
    description: 'Manage critical adjustments and understand system dependencies',
    duration: 55,
    icon: 'settings',
    color: '#7C3AED',
    overview: 'Develop the ability to understand complex system interactions, identify critical dependencies, and make adjustments without creating unintended consequences.',
    objectives: [
      'Map system dependencies within your district',
      'Identify critical adjustment points',
      'Understand cascading effects of changes',
      'Develop protocols for safe system modifications'
    ],
    lessons: [
      {
        id: 'skill2_l1',
        title: 'Systems Thinking Fundamentals',
        duration: 12,
        content: `A school district is a complex system of interconnected parts. Understanding how these parts interact is essential for effective risk management.

**What is Systems Thinking?**
Systems thinking is an approach that views the organization as a whole, recognizing that:
• Parts are interconnected and interdependent
• Changes in one area affect other areas
• The whole is greater than the sum of its parts
• Patterns and feedback loops drive behavior

**Key Systems in a School District:**
• **Educational System** - Curriculum, instruction, assessment
• **Operational System** - Facilities, transportation, food service
• **Human System** - Staffing, training, culture
• **Financial System** - Budgeting, purchasing, accounting
• **Information System** - Data, communication, technology
• **Safety System** - Security, emergency response, health

**System Properties:**
1. **Emergence** - System behaviors that arise from interactions
2. **Feedback** - How outputs influence inputs
3. **Delay** - Time lag between action and effect
4. **Equilibrium** - Tendency toward stable states
5. **Adaptation** - How systems respond to change

**Example: The Substitute Teacher System**
When a teacher is absent:
• Instruction quality may decrease
• Student behavior may change
• Other teachers may need to cover
• Office staff processes absence
• Substitute pay impacts budget
• Learning continuity is affected

One absence creates ripples across multiple systems.`,
        keyTakeaways: [
          'Schools are complex systems with many interconnections',
          'Changes create ripple effects across the organization',
          'Understanding system dynamics improves decision-making'
        ]
      },
      {
        id: 'skill2_l2',
        title: 'Mapping Dependencies',
        duration: 11,
        content: `Dependencies are the connections between system components where one relies on another. Unmapped dependencies are a major source of risk.

**Types of Dependencies:**

**1. Resource Dependencies**
• Staff skills and availability
• Equipment and facilities
• Budget and funding
• Supplies and materials

**2. Process Dependencies**
• Sequential processes (A must happen before B)
• Parallel processes (A and B must happen together)
• Conditional processes (If A, then B)

**3. Information Dependencies**
• Data required for decisions
• Communication channels
• Reporting relationships
• System integrations

**4. External Dependencies**
• Vendors and contractors
• Utilities and infrastructure
• Government agencies
• Partner organizations

**Dependency Mapping Exercise:**
For a critical process (e.g., morning bus routes):
1. List all inputs required
2. List all resources needed
3. Identify who/what provides each
4. Note single points of failure
5. Assess backup/redundancy

**Critical Dependencies in K-12:**
• Power and HVAC for building operations
• Internet for digital learning and communication
• Key personnel with specialized knowledge
• Vendor relationships for essential services
• State funding for financial stability

**The "What If" Test:**
For each dependency, ask:
• What if this fails or is unavailable?
• How long could we operate without it?
• What is our backup plan?
• Who needs to be notified?`,
        keyTakeaways: [
          'Dependencies create vulnerabilities that must be understood',
          'Single points of failure are high-priority risks',
          'Redundancy and backup plans reduce dependency risk'
        ]
      },
      {
        id: 'skill2_l3',
        title: 'Making Safe Adjustments',
        duration: 10,
        content: `When changes are needed, how do we implement them without creating new problems?

**The Change Risk Assessment:**
Before making any significant change, assess:
1. **Scope** - What systems/processes are affected?
2. **Dependencies** - What relies on what we're changing?
3. **Reversibility** - Can we undo this if needed?
4. **Timing** - When is the best time to implement?
5. **Communication** - Who needs to know?

**Principles for Safe Adjustment:**

**1. Start Small**
• Pilot changes in limited scope
• Test before full implementation
• Build in checkpoints

**2. Monitor Closely**
• Watch for unintended effects
• Check downstream systems
• Gather feedback quickly

**3. Move Incrementally**
• Break big changes into smaller steps
• Allow time for adjustment between steps
• Build on what works

**4. Maintain Reversibility**
• Keep rollback options available
• Document the previous state
• Don't burn bridges

**5. Communicate Proactively**
• Inform affected parties in advance
• Explain the rationale
• Provide support during transition

**Case Study: Schedule Change**
A district changed start times to address bus efficiency:
- Unintended effects on after-school activities
- Staff childcare arrangements disrupted
- Community athletics schedules conflicted
- Traffic patterns created new issues

The change solved one problem but created several others because system effects weren't fully considered.`,
        keyTakeaways: [
          'Every change carries risk of unintended consequences',
          'Systematic assessment reduces change-related problems',
          'Incremental implementation allows for course correction'
        ]
      },
      {
        id: 'skill2_l4',
        title: 'Feedback Loops and Continuous Improvement',
        duration: 12,
        content: `Effective systems have feedback mechanisms that enable continuous adjustment and improvement.

**Types of Feedback Loops:**

**Balancing Loops (Negative Feedback)**
These maintain stability:
• Thermostat maintaining temperature
• Budget controls limiting spending
• Discipline policies maintaining order

**Reinforcing Loops (Positive Feedback)**
These amplify change:
• Success breeding more success
• Problems leading to more problems
• Reputation effects (good or bad)

**Designing Effective Feedback:**

**1. Define What to Measure**
• Leading indicators (predictive)
• Lagging indicators (historical)
• Process metrics (how we work)
• Outcome metrics (what we achieve)

**2. Establish Measurement Systems**
• Automated data collection where possible
• Clear definitions and consistency
• Appropriate frequency
• Accessible reporting

**3. Create Response Protocols**
• Thresholds that trigger action
• Escalation procedures
• Assigned responsibilities
• Time-bound responses

**Feedback in Risk Management:**
• Incident reports → Investigation → Corrective action
• Near-miss reports → Trend analysis → Prevention
• Audit findings → Action plans → Verification
• Stakeholder complaints → Root cause → Improvement

**Common Feedback Failures:**
• Data collected but not analyzed
• Analysis done but not acted upon
• Actions taken but not followed up
• Successes not recognized or replicated`,
        keyTakeaways: [
          'Feedback loops are essential for system improvement',
          'Both balancing and reinforcing loops affect risk',
          'Effective feedback requires measurement, analysis, and action'
        ]
      },
      {
        id: 'skill2_l5',
        title: 'Building System Resilience',
        duration: 10,
        content: `Resilient systems can absorb disruption and continue functioning. Building resilience is a key goal of fine-tuning.

**Principles of System Resilience:**

**1. Redundancy**
• Backup systems and processes
• Cross-trained personnel
• Multiple communication channels
• Diverse vendor relationships

**2. Modularity**
• Independent components that can fail separately
• Clear boundaries between systems
• Ability to isolate problems
• Standardized interfaces

**3. Flexibility**
• Adaptable processes
• Multi-skilled workforce
• Scalable resources
• Adjustable priorities

**4. Diversity**
• Multiple approaches to key functions
• Varied skill sets in teams
• Different technologies
• Alternative suppliers

**Building Resilience in K-12:**

**Personnel Resilience**
• Cross-training for critical functions
• Succession planning
• Documentation of key processes
• Building institutional knowledge

**Operational Resilience**
• Backup facilities and equipment
• Manual overrides for automated systems
• Alternative vendors and suppliers
• Emergency operating procedures

**Information Resilience**
• Data backups and recovery
• Multiple communication methods
• Paper-based contingencies
• Distributed information access

**Testing Resilience:**
• Tabletop exercises
• Functional drills
• Full-scale exercises
• Unannounced tests`,
        keyTakeaways: [
          'Resilience enables continued operation despite disruptions',
          'Redundancy, modularity, flexibility, and diversity build resilience',
          'Regular testing validates resilience measures'
        ]
      }
    ]
  },
  {
    id: 'skill3',
    title: 'Boost Confidence',
    description: 'Create safety margins and enhance organizational capabilities',
    duration: 50,
    icon: 'shield',
    color: '#059669',
    overview: 'Learn to build genuine confidence through capability development, safety margins, and stakeholder engagement rather than false assurance.',
    objectives: [
      'Assess current capability levels honestly',
      'Build appropriate safety margins into operations',
      'Develop staff competencies systematically',
      'Secure stakeholder support for risk initiatives'
    ],
    lessons: [
      {
        id: 'skill3_l1',
        title: 'True Confidence vs. False Assurance',
        duration: 10,
        content: `There's a critical difference between genuine confidence based on capability and false assurance based on hope or ignorance.

**Signs of False Assurance:**
• "We've never had a problem before"
• "Our insurance will cover it"
• "That's someone else's responsibility"
• "We have a policy for that" (but no one follows it)
• "Our staff is trained" (but training was years ago)

**Signs of True Confidence:**
• Evidence-based assessment of capabilities
• Regular testing and validation
• Honest acknowledgment of gaps
• Active improvement efforts
• Appropriate humility about uncertainty

**The Confidence-Competence Matrix:**

**High Confidence/High Competence** = Justified Confidence
• Continue developing and maintaining
• Share knowledge with others
• Monitor for complacency

**High Confidence/Low Competence** = Dangerous Overconfidence
• Most dangerous quadrant
• Reality check urgently needed
• Address gaps immediately

**Low Confidence/High Competence** = Unrecognized Strength
• Build awareness of capabilities
• Leverage existing strengths
• Communicate successes

**Low Confidence/Low Competence** = Appropriate Concern
• Acknowledge and address gaps
• Seek help and resources
• Prioritize improvement

**Building Justified Confidence:**
1. Honestly assess current state
2. Identify gaps and weaknesses
3. Develop improvement plans
4. Implement and test
5. Verify effectiveness
6. Maintain and update`,
        keyTakeaways: [
          'False assurance is more dangerous than acknowledged weakness',
          'True confidence is based on demonstrated capability',
          'Regular validation maintains justified confidence'
        ]
      },
      {
        id: 'skill3_l2',
        title: 'Building Safety Margins',
        duration: 10,
        content: `Safety margins are buffers that protect against unexpected events. They provide room for error and time for response.

**Types of Safety Margins:**

**Time Margins**
• Buffer time in schedules
• Early warning systems
• Response time allowances
• Deadline cushions

**Resource Margins**
• Reserve funds
• Backup equipment
• Extra supplies
• Surge staffing capacity

**Capacity Margins**
• Room for growth
• Ability to absorb increases
• Flexibility in utilization
• Scalable systems

**Process Margins**
• Multiple approval steps
• Verification checks
• Redundant controls
• Error detection points

**Where Margins Matter Most in K-12:**

**Student Safety**
• Supervision ratios above minimum
• Emergency equipment readily accessible
• Multiple communication methods
• Backup procedures for critical functions

**Financial**
• Reserve funds (typically 5-10% of budget)
• Contingency lines in budgets
• Insurance coverage above expected losses
• Cash flow buffers

**Staffing**
• Cross-trained personnel
• Substitute pools
• Succession plans
• Overtime capacity

**Facilities**
• Maintenance reserves
• Spare equipment
• Alternative spaces
• Utility redundancy

**Calculating Appropriate Margins:**
Consider:
• Probability of needing the margin
• Cost of maintaining the margin
• Consequence of not having it
• Recovery time if margin is consumed`,
        keyTakeaways: [
          'Safety margins provide protection against uncertainty',
          'Different types of margins address different risks',
          'The cost of margins must be weighed against benefits'
        ]
      },
      {
        id: 'skill3_l3',
        title: 'Capability Assessment and Development',
        duration: 12,
        content: `Systematic capability assessment identifies where you need to develop capacity.

**Assessing Current Capabilities:**

**Individual Level**
• Skills and knowledge inventory
• Certification and training records
• Performance assessments
• Gap analysis against requirements

**Team Level**
• Collective expertise
• Collaboration effectiveness
• Coverage and redundancy
• Response capability

**Organizational Level**
• Process maturity
• Resource adequacy
• System effectiveness
• Culture and climate

**Capability Assessment Framework:**

For each critical function, assess:
1. **Availability** - Do we have this capability?
2. **Proficiency** - How well can we perform?
3. **Reliability** - Can we perform consistently?
4. **Scalability** - Can we perform under stress?
5. **Sustainability** - Can we maintain this over time?

**Rating Scale:**
1 = Absent or severely limited
2 = Basic/developing
3 = Adequate/functional
4 = Strong/reliable
5 = Excellent/best practice

**Developing Capabilities:**

**Training Approaches**
• Formal instruction
• On-the-job learning
• Mentoring and coaching
• Self-directed learning
• Practice and drills

**Development Planning**
1. Identify capability gaps
2. Prioritize based on risk
3. Select development methods
4. Allocate resources
5. Implement programs
6. Assess effectiveness

**Building Institutional Capability**
• Document processes and procedures
• Create knowledge repositories
• Establish communities of practice
• Develop training programs
• Ensure succession planning`,
        keyTakeaways: [
          'Capability assessment must be honest and systematic',
          'Development should be prioritized by risk',
          'Both individual and organizational capabilities matter'
        ]
      },
      {
        id: 'skill3_l4',
        title: 'Stakeholder Engagement and Buy-In',
        duration: 10,
        content: `Risk management initiatives succeed when stakeholders are engaged and supportive.

**Key Stakeholder Groups:**

**Internal Stakeholders**
• Board of Education
• Superintendent and Cabinet
• Principals and administrators
• Teachers and staff
• Students (age-appropriate)

**External Stakeholders**
• Parents and families
• Community members
• Regulators and agencies
• Insurance carriers
• Media

**Engagement Strategies by Group:**

**Board of Education**
• Regular risk reporting
• Policy recommendations
• Financial impact data
• Benchmarking information
• Success stories

**Administrators**
• Practical tools and resources
• Clear expectations
• Support and training
• Recognition for compliance
• Involvement in planning

**Teachers and Staff**
• Relevance to their work
• Input on solutions
• Adequate training time
• Resources and equipment
• Appreciation for efforts

**Parents**
• Transparency about efforts
• Involvement opportunities
• Communication channels
• Responsiveness to concerns
• Partnership approach

**Building Support for Risk Initiatives:**

**Make the Case**
• Connect to mission (student success)
• Show cost-benefit
• Use data and evidence
• Share peer practices
• Tell stories

**Involve Stakeholders**
• Seek input early
• Include in planning
• Address concerns
• Acknowledge contributions
• Share ownership

**Communicate Effectively**
• Tailor messages to audience
• Use multiple channels
• Be consistent and honest
• Celebrate successes
• Acknowledge challenges`,
        keyTakeaways: [
          'Stakeholder support is essential for risk management success',
          'Different stakeholders need different engagement approaches',
          'Involvement and communication build buy-in'
        ]
      },
      {
        id: 'skill3_l5',
        title: 'Celebrating Success and Building Momentum',
        duration: 8,
        content: `Recognizing and celebrating risk management successes builds confidence and sustains momentum.

**Why Celebrate Success?**
• Reinforces desired behaviors
• Builds organizational confidence
• Demonstrates value of efforts
• Motivates continued improvement
• Creates positive culture

**What to Celebrate:**

**Prevention Successes**
• Hazards identified and corrected
• Near-misses reported and addressed
• Compliance maintained
• Audits passed
• Training completed

**Response Successes**
• Incidents handled well
• Quick recovery from disruption
• Effective emergency response
• Good crisis communication
• Lessons learned applied

**Improvement Successes**
• Capability gaps closed
• New systems implemented
• Processes improved
• Metrics improved
• Goals achieved

**How to Celebrate:**

**Individual Recognition**
• Personal thanks
• Public acknowledgment
• Awards and incentives
• Development opportunities
• Leadership visibility

**Team Recognition**
• Team celebrations
• Shared rewards
• Public recognition
• Success stories
• Learning sharing

**Organizational Recognition**
• All-hands communication
• Annual reports
• Board presentations
• Community sharing
• Media coverage

**Sustaining Momentum:**
• Set progressive goals
• Track and share metrics
• Tell ongoing stories
• Connect to mission
• Maintain leadership focus`,
        keyTakeaways: [
          'Celebrating success reinforces positive behaviors',
          'Recognition should be timely and appropriate',
          'Sustained celebration builds lasting culture'
        ]
      }
    ]
  },
  {
    id: 'skill4',
    title: 'Balance Exposures',
    description: 'Distinguish acceptable risks and define tolerance levels',
    duration: 60,
    icon: 'scale',
    color: '#DC2626',
    overview: 'Master the art of balancing risk and reward, understanding that not all risks should be avoided and that informed risk-taking can create sustainable advantage.',
    objectives: [
      'Understand risk appetite vs. risk tolerance',
      'Categorize risks appropriately',
      'Make informed decisions about risk acceptance',
      'Balance the overall risk portfolio'
    ],
    lessons: [
      {
        id: 'skill4_l1',
        title: 'Risk Appetite and Risk Tolerance',
        duration: 12,
        content: `Understanding your district's capacity and willingness to accept risk is foundational to balanced risk management.

**Definitions:**

**Risk Appetite**
The amount and type of risk an organization is willing to accept in pursuit of its objectives. This is a strategic-level concept.

**Risk Tolerance**
The acceptable variation in outcomes relative to achieving objectives. This is operational-level and more specific.

**Risk Capacity**
The maximum amount of risk an organization can absorb. This is an absolute limit.

**Example:**
• Risk Capacity: $2M in reserves could absorb losses up to that amount
• Risk Appetite: We're willing to accept moderate financial risk for innovative programs
• Risk Tolerance: Individual program losses should not exceed $50K

**Factors Influencing Risk Appetite:**

**Mission and Values**
• Student safety = very low appetite for physical harm
• Educational innovation = higher appetite for program risk
• Fiscal responsibility = moderate appetite for financial risk

**Stakeholder Expectations**
• Parents expect high safety standards
• Board expects fiscal prudence
• Community expects transparency

**Organizational Capacity**
• Financial reserves
• Management capability
• Recovery resources
• Insurance coverage

**External Environment**
• Regulatory requirements
• Legal exposure
• Competitive pressures
• Economic conditions

**Articulating Risk Appetite:**
For each risk category, define:
• What risks are we willing to accept?
• What is unacceptable regardless of benefit?
• What level of uncertainty can we manage?
• How much loss could we absorb?`,
        keyTakeaways: [
          'Risk appetite is strategic; risk tolerance is operational',
          'Different risk categories warrant different appetite levels',
          'Clear articulation guides decision-making'
        ]
      },
      {
        id: 'skill4_l2',
        title: 'Risk Categorization',
        duration: 10,
        content: `Not all risks are equal. Effective risk management requires categorizing risks to enable appropriate treatment.

**Risk Categories in K-12:**

**Safety and Security**
• Student physical safety
• Staff workplace safety
• Campus security
• Emergency events

**Operational**
• Transportation
• Facilities
• Food service
• Technology systems

**Financial**
• Revenue/funding
• Expenditures
• Investments
• Insurance

**Legal and Compliance**
• Regulatory compliance
• Contractual obligations
• Litigation exposure
• Policy violations

**Reputational**
• Community perception
• Media coverage
• Stakeholder trust
• Brand impact

**Strategic**
• Mission achievement
• Competitive position
• Long-term sustainability
• Change initiatives

**Categorizing by Acceptability:**

**Unacceptable Risks** - Never accept regardless of benefit
• Student harm that could be prevented
• Legal/ethical violations
• Catastrophic financial loss
• Irreversible damage

**Tolerable Risks** - Accept with appropriate controls
• Manageable financial exposure
• Recoverable operational disruptions
• Repairable reputation impacts
• Insurable losses

**Acceptable Risks** - Part of normal operations
• Minor day-to-day variations
• Expected operational challenges
• Planned risk-taking for improvement
• Insured/transferred risks

**Opportunity Risks** - Seek deliberately
• Calculated innovation
• Strategic investments
• Competitive advantages
• Growth initiatives`,
        keyTakeaways: [
          'Risk categorization enables appropriate treatment',
          'Not all risks should be avoided',
          'Some risks should be actively pursued'
        ]
      },
      {
        id: 'skill4_l3',
        title: 'Risk Treatment Options',
        duration: 12,
        content: `Once risks are identified and categorized, decisions must be made about how to treat them.

**The Four T's of Risk Treatment:**

**1. Terminate (Avoid)**
• Eliminate the activity that creates the risk
• Exit the exposure entirely
• Choose not to pursue opportunity

When to Terminate:
• Risk exceeds appetite with no mitigation possible
• Activity is not essential to mission
• Exposure is unacceptable regardless of controls

**2. Treat (Mitigate)**
• Implement controls to reduce likelihood
• Implement controls to reduce impact
• Improve detection and response

When to Treat:
• Risk can be reduced to acceptable levels
• Controls are cost-effective
• Activity is important to mission

**3. Transfer**
• Insurance (financial transfer)
• Contracts (legal transfer)
• Outsourcing (operational transfer)
• Partnerships (shared risk)

When to Transfer:
• Others can manage risk better
• Transfer is cost-effective
• Residual risk is acceptable
• Transfer is available

**4. Tolerate (Accept)**
• Accept the risk without additional action
• Monitor and respond if risk materializes
• Self-insure

When to Tolerate:
• Risk is within appetite
• Cost of treatment exceeds benefit
• Risk is inherent to mission
• Treatment is not available

**Making Treatment Decisions:**
Consider:
• Cost of treatment vs. exposure
• Effectiveness of treatment options
• Feasibility of implementation
• Residual risk after treatment
• Side effects of treatment`,
        keyTakeaways: [
          'Multiple treatment options exist for every risk',
          'Treatment decisions should be deliberate and documented',
          'Cost-benefit analysis guides treatment selection'
        ]
      },
      {
        id: 'skill4_l4',
        title: 'Calculated Risk-Taking',
        duration: 14,
        content: `Risk intelligence includes knowing when and how to take risks that create sustainable advantage.

**When to Take Calculated Risks:**

**Mission Advancement**
• Educational innovation that could improve outcomes
• New programs that serve unmet needs
• Technology adoption that enhances capability

**Competitive Position**
• Attracting and retaining students
• Recruiting top talent
• Building community support
• Establishing leadership

**Efficiency and Effectiveness**
• Process improvements with uncertainty
• Investment in new capabilities
• Organizational changes

**Requirements for Calculated Risk-Taking:**

**1. Clear Objectives**
• What are we trying to achieve?
• How does this advance our mission?
• What would success look like?

**2. Informed Decision**
• What are the potential outcomes?
• What is the probability of each?
• What is the magnitude of each?
• What are the alternatives?

**3. Acceptable Downside**
• What is the worst-case scenario?
• Can we absorb that outcome?
• Is the downside reversible?
• Are stakeholders informed?

**4. Upside Potential**
• What is the best-case scenario?
• Is the potential reward worth the risk?
• Is this opportunity unique?
• What would we miss by not acting?

**5. Mitigation Plan**
• How will we reduce downside risk?
• How will we detect problems early?
• What is our exit strategy?
• How will we respond if risk materializes?

**Case Study: 1:1 Device Program**
A district considers providing devices to all students:
• Risk: Significant cost, technology challenges, instructional disruption
• Opportunity: Enhanced learning, equity, future preparation
• Mitigation: Phased rollout, professional development, insurance
• Decision: Proceed with controlled pilot before full implementation`,
        keyTakeaways: [
          'Calculated risk-taking is essential for advancement',
          'Risks should be taken deliberately, not by default',
          'Mitigation and exit strategies reduce downside'
        ]
      },
      {
        id: 'skill4_l5',
        title: 'Portfolio Management',
        duration: 12,
        content: `Looking at risks individually is not enough. The overall portfolio of risks must be balanced.

**Portfolio Thinking:**
• Individual risks aggregate
• Correlations matter (risks that occur together)
• Concentrations create vulnerability
• Diversification provides protection

**Portfolio Considerations:**

**Concentration Risk**
• Over-reliance on single revenue source
• Dependence on key personnel
• Geographic concentration
• Vendor concentration

**Correlation Risk**
• Economic downturn affects multiple risks simultaneously
• Weather events impact multiple operations
• Technology failure cascades across systems
• Reputation issues compound

**Timing Risk**
• Multiple initiatives competing for resources
• Seasonal risk overlaps
• Budget cycle pressures
• Change fatigue

**Balancing the Portfolio:**

**Diversification**
• Multiple revenue streams
• Varied program offerings
• Distributed operations
• Multiple vendors

**Hedging**
• Insurance coverage
• Reserve funds
• Contingency plans
• Backup systems

**Timing**
• Stagger major initiatives
• Sequence high-risk activities
• Align with resource availability
• Build in recovery time

**Monitoring**
• Aggregate risk dashboards
• Correlation analysis
• Stress testing
• Scenario planning

**Portfolio Risk Reporting:**
Create a dashboard showing:
• Overall risk exposure
• Distribution across categories
• Concentration points
• Trend over time
• Comparison to appetite`,
        keyTakeaways: [
          'Risks must be viewed as a portfolio, not individually',
          'Concentration and correlation amplify risk',
          'Diversification and hedging balance the portfolio'
        ]
      }
    ]
  },
  {
    id: 'skill5',
    title: 'Perpetuate Ownership',
    description: 'Foster commitment and individual responsibility for risk',
    duration: 45,
    icon: 'users',
    color: '#0891B2',
    overview: 'Create a culture where everyone takes ownership for risk management, knowledge is preserved, and accountability is clear.',
    objectives: [
      'Build risk ownership at all levels',
      'Preserve and transfer risk knowledge',
      'Establish clear accountability structures',
      'Sustain risk culture over time'
    ],
    lessons: [
      {
        id: 'skill5_l1',
        title: 'Creating a Culture of Ownership',
        duration: 15,
        content: `Risk management cannot be the responsibility of one person or department. Everyone must own risk in their domain.

**Why Ownership Matters:**
• Risks are everywhere; no central function can see them all
• Those closest to work understand risks best
• Action requires local authority and capability
• Sustainable culture outlasts individual champions

**Barriers to Ownership:**
• "That's not my job"
• "Risk management will handle it"
• "I don't have authority to do anything"
• "I'll get blamed if I raise concerns"
• "We've always done it this way"

**Building Ownership:**

**1. Clear Expectations**
• Define risk responsibilities for each role
• Include in job descriptions
• Address in performance reviews
• Communicate consistently

**2. Authority to Act**
• Empower employees to address risks
• Define decision authority clearly
• Support risk-informed decisions
• Remove barriers to action

**3. Resources and Capability**
• Provide training and tools
• Allocate time for risk activities
• Offer expert support when needed
• Invest in improvement

**4. Accountability**
• Monitor risk performance
• Recognize positive contributions
• Address failures constructively
• Link to consequences

**5. Leadership Modeling**
• Leaders demonstrate ownership
• Risk is a regular leadership topic
• Resources are allocated
• Priorities are clear

**The RACI Model for Risk:**
• **Responsible** - Who performs risk activities?
• **Accountable** - Who is ultimately answerable?
• **Consulted** - Whose input is needed?
• **Informed** - Who needs to know?`,
        keyTakeaways: [
          'Risk ownership must be distributed throughout the organization',
          'Clear expectations and authority enable ownership',
          'Leadership modeling sets the tone'
        ]
      },
      {
        id: 'skill5_l2',
        title: 'Knowledge Preservation and Transfer',
        duration: 15,
        content: `Institutional knowledge about risks is a critical asset that must be preserved and transferred.

**The Knowledge Challenge:**
• Key personnel retire or leave
• Memory of past incidents fades
• Lessons learned are forgotten
• New staff lack historical context

**Types of Risk Knowledge:**

**Explicit Knowledge**
• Documented procedures
• Written policies
• Training materials
• Incident reports
• Audit findings

**Tacit Knowledge**
• Experience and judgment
• Relationships and networks
• Informal practices
• Cultural understanding
• Intuition about risks

**Preservation Strategies:**

**Documentation**
• Comprehensive procedures
• Decision rationale records
• Incident and near-miss reports
• Lessons learned databases
• Risk register maintenance

**Knowledge Capture**
• Exit interviews
• Mentoring relationships
• Video/audio recordings
• Story documentation
• Expert interviews

**Institutionalization**
• Training programs
• Orientation content
• Communities of practice
• Regular briefings
• Knowledge repositories

**Transfer Mechanisms:**

**Formal Transfer**
• Structured training
• Certification programs
• Written handoffs
• Scheduled briefings

**Informal Transfer**
• Mentoring relationships
• Job shadowing
• Team collaboration
• Storytelling
• Informal networks

**Technology Support:**
• Document management systems
• Knowledge bases
• Video libraries
• Collaboration platforms
• Risk management information systems`,
        keyTakeaways: [
          'Both explicit and tacit risk knowledge must be preserved',
          'Multiple mechanisms ensure effective knowledge transfer',
          'Technology can support but not replace human knowledge transfer'
        ]
      },
      {
        id: 'skill5_l3',
        title: 'Accountability Without Blame',
        duration: 15,
        content: `Effective risk cultures balance accountability with psychological safety.

**The Accountability Paradox:**
• We need accountability to ensure performance
• Blame cultures suppress reporting and learning
• Fear drives hiding rather than addressing risks
• Yet some failures warrant consequences

**Just Culture Framework:**

**Human Error**
• Unintentional mistakes
• Response: Console, coach, improve systems

**At-Risk Behavior**
• Risky choices that seem reasonable at the time
• Response: Coach, remove incentives for risk, improve environment

**Reckless Behavior**
• Conscious disregard for substantial risk
• Response: Consequences, remediation, potential discipline

**The Reporting Climate:**
To encourage reporting:
• Separate reporting from blame
• Focus on system improvement
• Respond positively to reports
• Protect reporters from retaliation
• Celebrate risk identification

**Accountability Structures:**

**Individual Accountability**
• Clear responsibilities
• Performance expectations
• Feedback and coaching
• Recognition and consequences

**Management Accountability**
• Resources and support
• Culture and climate
• System effectiveness
• Staff development

**Organizational Accountability**
• Governance oversight
• Policy adequacy
• Resource allocation
• External accountability

**After Incidents:**
• Conduct fair investigation
• Identify system factors
• Determine human factors
• Apply appropriate response
• Improve systems
• Communicate lessons`,
        keyTakeaways: [
          'Accountability and psychological safety can coexist',
          'Just culture differentiates error, at-risk, and reckless behavior',
          'Systems often contribute more than individuals to failures'
        ]
      }
    ]
  },
  {
    id: 'skill6',
    title: 'Set Perimeter',
    description: 'Judge event relevance and establish appropriate boundaries',
    duration: 45,
    icon: 'layout',
    color: '#4F46E5',
    overview: 'Learn to define the scope of your risk management focus, filtering signal from noise and establishing appropriate time horizons.',
    objectives: [
      'Define risk management scope appropriately',
      'Filter relevant information from noise',
      'Establish appropriate time horizons',
      'Maintain boundaries while staying adaptable'
    ],
    lessons: [
      {
        id: 'skill6_l1',
        title: 'Defining Your Risk Scope',
        duration: 15,
        content: `You cannot manage all risks. Defining scope focuses effort where it matters most.

**Scope Considerations:**

**What's In Scope:**
• Direct operations and activities
• Clear legal/regulatory obligations
• Significant financial exposures
• Core mission delivery
• Stakeholder expectations

**What's Out of Scope:**
• Events beyond control or influence
• De minimis exposures
• Others' responsibilities
• Highly speculative risks
• Theoretical possibilities

**Setting Boundaries:**

**Organizational Boundaries**
• Which entities/locations are included?
• What about contractors and partners?
• Where does our responsibility end?
• What about shared spaces/activities?

**Risk Type Boundaries**
• Which categories do we actively manage?
• What's handled elsewhere (e.g., by insurers)?
• What's accepted without active management?
• What emerging risks do we monitor?

**Activity Boundaries**
• Which activities are subject to risk processes?
• What level triggers formal assessment?
• When do we involve specialists?
• What's managed informally?

**Scope Documentation:**
Create a clear statement:
"Our risk management program covers [entities] with respect to [risk categories] for [activities above threshold]. We monitor but do not actively manage [specific exclusions]. [Other party] is responsible for [their scope]."`,
        keyTakeaways: [
          'Clear scope focuses effort and prevents gaps',
          'Boundaries should be documented and communicated',
          'Scope should be reviewed periodically'
        ]
      },
      {
        id: 'skill6_l2',
        title: 'Information Filtering',
        duration: 15,
        content: `The volume of information about potential risks is overwhelming. Effective filtering separates signal from noise.

**The Information Overload Challenge:**
• Constant news about disasters, incidents, threats
• Multiple information sources and channels
• Limited time and attention
• Everything seems urgent

**Filtering Framework:**

**Relevance Test**
• Does this affect our operations?
• Are our risk factors similar?
• Could this happen here?
• Do we have similar exposures?

**Probability Assessment**
• How likely is this for us?
• What would need to happen?
• What are the preconditions?
• What are the trends?

**Impact Assessment**
• What would be the effect on us?
• Is this within our risk appetite?
• Do we have existing controls?
• How would we respond?

**Actionability Test**
• Can we do something about this?
• Is action needed now?
• What would action look like?
• Do we have the capability?

**Information Sources:**
Prioritize:
• Industry-specific sources (education associations)
• Regulatory updates
• Professional publications
• Peer network information
• Local/regional news

Limit:
• General news media
• Social media speculation
• Vendor fear marketing
• Unverified reports

**Creating an Information Diet:**
• Identify trusted sources
• Establish regular review times
• Delegate scanning to appropriate staff
• Create synthesis and briefing processes
• Avoid constant monitoring`,
        keyTakeaways: [
          'Not all risk information requires attention',
          'Systematic filtering prevents overwhelm',
          'Focus on actionable, relevant information'
        ]
      },
      {
        id: 'skill6_l3',
        title: 'Time Horizons',
        duration: 15,
        content: `Different risks require different time horizons for planning and management.

**Time Horizon Categories:**

**Immediate (0-30 days)**
• Daily operational risks
• Current compliance requirements
• Active incidents and issues
• Near-term events

**Short-term (1-12 months)**
• Seasonal risks
• Annual compliance
• Budget cycle risks
• Project risks

**Medium-term (1-3 years)**
• Strategic initiative risks
• Demographic changes
• Facility planning
• Staff development

**Long-term (3+ years)**
• Major capital projects
• Environmental changes
• Technology evolution
• Retirement/succession

**Matching Time Horizons:**

**Quick Response Risks**
• Safety incidents
• Weather emergencies
• Technology failures
• Security threats

**Planned Response Risks**
• Compliance deadlines
• Budget constraints
• Staff changes
• Facility maintenance

**Strategic Planning Risks**
• Enrollment trends
• Funding changes
• Regulatory evolution
• Community changes

**Time Horizon Discipline:**
• Match planning timeframe to risk nature
• Don't apply short-term thinking to long-term risks
• Don't over-analyze immediate operational risks
• Build appropriate review cycles for each horizon
• Connect horizons (short-term actions support long-term goals)`,
        keyTakeaways: [
          'Different risks require different time horizons',
          'Match planning and review cycles to risk timeframes',
          'Short-term actions should support long-term goals'
        ]
      }
    ]
  },
  {
    id: 'skill7',
    title: 'Scrub Data',
    description: 'Verify sources and address cognitive biases',
    duration: 50,
    icon: 'search',
    color: '#DB2777',
    overview: 'Develop skills to ensure the information driving risk decisions is accurate, unbiased, and properly interpreted.',
    objectives: [
      'Verify information sources and quality',
      'Recognize and mitigate cognitive biases',
      'Cross-reference and validate data',
      'Maintain data quality over time'
    ],
    lessons: [
      {
        id: 'skill7_l1',
        title: 'Source Verification',
        duration: 12,
        content: `The quality of risk decisions depends on the quality of information. Verification is essential.

**Source Quality Criteria:**

**Authority**
• Who created this information?
• What are their qualifications?
• Do they have relevant expertise?
• What is their reputation?

**Accuracy**
• Is the information factually correct?
• Has it been verified?
• Are there known errors?
• Does it match other sources?

**Currency**
• When was this created?
• Is it still valid?
• Have circumstances changed?
• When was it last updated?

**Objectivity**
• Is there potential bias?
• What is the source's perspective?
• Are there conflicts of interest?
• Is it balanced?

**Coverage**
• How comprehensive is it?
• What is excluded?
• What are the limitations?
• Is the scope appropriate?

**Verification Methods:**

**Cross-referencing**
• Compare multiple sources
• Look for confirmation
• Note discrepancies
• Investigate differences

**Primary Source Review**
• Go to original source when possible
• Verify citations
• Check context
• Understand methodology

**Expert Consultation**
• Ask subject matter experts
• Seek second opinions
• Consult professional networks
• Engage specialists

**Red Flags:**
• Unattributed information
• Emotional language
• Lack of detail
• Unverifiable claims
• Single source
• Outdated information`,
        keyTakeaways: [
          'Source quality directly affects decision quality',
          'Multiple verification methods should be used',
          'Red flags warrant additional scrutiny'
        ]
      },
      {
        id: 'skill7_l2',
        title: 'Recognizing Cognitive Biases',
        duration: 14,
        content: `Our brains take shortcuts that can lead to flawed risk judgments. Awareness is the first step to mitigation.

**Common Biases in Risk Management:**

**Availability Bias**
• Recent or vivid events seem more likely
• What's in the news feels more probable
• Personal experience outweighs statistics

*Mitigation:* Use data, not memory; consider base rates

**Confirmation Bias**
• We seek information that confirms existing beliefs
• We discount contradictory evidence
• We interpret ambiguity in our favor

*Mitigation:* Actively seek disconfirming evidence; devil's advocate

**Overconfidence**
• We overestimate our knowledge and ability
• We underestimate uncertainty
• We believe we're better than average

*Mitigation:* Track prediction accuracy; seek outside views

**Anchoring**
• First information received dominates
• Adjustments from anchor are insufficient
• Arbitrary numbers influence judgment

*Mitigation:* Consider multiple starting points; question anchors

**Groupthink**
• Desire for consensus suppresses dissent
• Group reinforces shared views
• Warnings are dismissed

*Mitigation:* Encourage dissent; anonymous input; outside review

**Normalcy Bias**
• Belief that things will continue as they have
• Underestimation of change probability
• Slow response to warning signs

*Mitigation:* Scenario planning; historical analysis; external scanning

**Hindsight Bias**
• Past events seem more predictable than they were
• We forget what we didn't know
• We judge past decisions by outcomes

*Mitigation:* Document decisions and rationale; prospective analysis`,
        keyTakeaways: [
          'Everyone is subject to cognitive biases',
          'Awareness enables mitigation strategies',
          'Systematic processes can counteract biases'
        ]
      },
      {
        id: 'skill7_l3',
        title: 'Data Corroboration and Validation',
        duration: 12,
        content: `Single data points can be misleading. Corroboration and validation improve confidence.

**Corroboration Methods:**

**Triangulation**
• Multiple data sources
• Different collection methods
• Various perspectives
• Independent verification

**Trend Analysis**
• Historical comparison
• Pattern identification
• Anomaly detection
• Trajectory assessment

**Benchmarking**
• Peer comparison
• Industry standards
• Best practices
• External norms

**Expert Review**
• Subject matter validation
• Technical verification
• Practical feasibility check
• Experience-based assessment

**Validation Techniques:**

**Data Quality Checks**
• Completeness review
• Accuracy verification
• Consistency testing
• Timeliness assessment

**Reasonableness Tests**
• Does this make sense?
• Is it consistent with other information?
• Are there logical explanations?
• Do the numbers add up?

**Sensitivity Analysis**
• What if assumptions change?
• How robust are conclusions?
• What's the range of outcomes?
• Where are the critical dependencies?

**When to Dig Deeper:**
• Data seems too good/bad to be true
• Significant decisions depend on data
• Sources are limited or questionable
• Inconsistencies exist
• Stakes are high`,
        keyTakeaways: [
          'Corroboration increases confidence in data',
          'Multiple validation techniques should be used',
          'High-stakes decisions warrant deeper validation'
        ]
      },
      {
        id: 'skill7_l4',
        title: 'Maintaining Data Quality',
        duration: 12,
        content: `Data quality requires ongoing attention. Processes must be in place to maintain accuracy.

**Data Quality Dimensions:**

**Accuracy**
• Data correctly represents reality
• Errors are minimized
• Updates are made promptly

**Completeness**
• All necessary data is captured
• Gaps are identified and addressed
• Coverage is appropriate

**Consistency**
• Data is uniform across sources
• Definitions are standardized
• Formats are compatible

**Timeliness**
• Data is current
• Updates are timely
• Historical data is preserved

**Accessibility**
• Data is available when needed
• Users can find what they need
• Formats support use

**Quality Maintenance Practices:**

**Data Governance**
• Clear ownership
• Defined standards
• Documented procedures
• Assigned responsibilities

**Regular Review**
• Periodic audits
• Accuracy checks
• Completeness assessment
• User feedback

**Error Correction**
• Reporting mechanisms
• Correction procedures
• Root cause analysis
• Prevention measures

**Technology Support**
• Validation rules
• Automated checks
• Quality dashboards
• Alert systems

**Common Data Problems:**
• Manual entry errors
• Outdated information
• Duplicate records
• Inconsistent coding
• Missing fields
• System integration issues`,
        keyTakeaways: [
          'Data quality requires ongoing maintenance',
          'Clear governance and processes are essential',
          'Technology can support but not replace quality practices'
        ]
      }
    ]
  },
  {
    id: 'skill8',
    title: 'Place Sensors',
    description: 'Risk sensing and early warning signal detection',
    duration: 50,
    icon: 'radio',
    color: '#F59E0B',
    overview: 'Build systems to detect emerging risks early, enabling proactive response rather than reactive crisis management.',
    objectives: [
      'Design effective early warning systems',
      'Identify and monitor leading indicators',
      'Build risk sensing networks',
      'Respond appropriately to warning signals'
    ],
    lessons: [
      {
        id: 'skill8_l1',
        title: 'Early Warning System Design',
        duration: 12,
        content: `Early warning systems provide advance notice of emerging risks, enabling proactive response.

**Components of Early Warning Systems:**

**1. Indicator Identification**
• What signals precede the risk event?
• What can be measured or observed?
• How much lead time is provided?
• How reliable is the indicator?

**2. Monitoring Mechanisms**
• How will indicators be tracked?
• Who is responsible for monitoring?
• What is the monitoring frequency?
• How is data collected?

**3. Threshold Setting**
• What level triggers concern?
• What level requires action?
• How are thresholds determined?
• How are they updated?

**4. Alert Processes**
• How are alerts communicated?
• Who receives alerts?
• What is expected response?
• How is escalation handled?

**5. Response Protocols**
• What actions follow alerts?
• Who has authority to act?
• What resources are available?
• How is response coordinated?

**Examples in K-12:**

**Student Safety**
• Attendance patterns
• Discipline referrals
• Counselor referrals
• Threat indicators

**Financial**
• Cash flow trends
• Enrollment projections
• Revenue indicators
• Expenditure variances

**Operational**
• Maintenance requests
• Equipment failures
• Inspection findings
• Complaint trends`,
        keyTakeaways: [
          'Early warning systems enable proactive risk management',
          'Multiple components must work together',
          'Design should be tailored to specific risks'
        ]
      },
      {
        id: 'skill8_l2',
        title: 'Leading vs. Lagging Indicators',
        duration: 12,
        content: `Effective risk sensing requires a balance of leading and lagging indicators.

**Lagging Indicators**
Measure outcomes after they occur:
• Incident counts
• Claims costs
• Audit findings
• Complaint numbers

*Value:* Provide definitive measurement
*Limitation:* Too late to prevent

**Leading Indicators**
Predict future outcomes:
• Near-miss reports
• Training completion
• Inspection results
• Safety observations

*Value:* Enable prevention
*Limitation:* Uncertain prediction

**Finding Leading Indicators:**

**Work Backward**
• What causes the outcome we want to prevent?
• What precedes that cause?
• What can we observe earlier?
• What correlates with outcomes?

**Look for Patterns**
• What happened before past incidents?
• What conditions were present?
• What was different from normal?
• What warning signs were missed?

**Consult Research**
• What does literature indicate?
• What do peers track?
• What do experts recommend?
• What have others learned?

**Leading Indicators in K-12:**

**Safety**
• Safety observation frequency
• Hazard reporting rate
• Training currency
• Drill performance

**Personnel**
• Employee satisfaction scores
• Voluntary turnover early signals
• Grievance trends
• Absenteeism patterns

**Student**
• Early academic indicators
• Attendance trends
• Behavior referral patterns
• Social-emotional screening

**Facilities**
• Preventive maintenance completion
• Work order aging
• Inspection deficiencies
• Energy consumption anomalies`,
        keyTakeaways: [
          'Leading indicators enable prevention',
          'Both leading and lagging indicators are needed',
          'Finding good leading indicators requires analysis'
        ]
      },
      {
        id: 'skill8_l3',
        title: 'Building Sensing Networks',
        duration: 14,
        content: `Risk sensing requires human networks in addition to technical systems.

**The Human Sensor Network:**
Every person in the organization can be a risk sensor:
• Teachers observing student behavior
• Custodians noticing facility issues
• Office staff hearing concerns
• Bus drivers seeing route hazards
• Students reporting problems

**Building the Network:**

**1. Create Awareness**
• Train staff to recognize risks
• Communicate what to watch for
• Share examples and scenarios
• Explain the importance

**2. Enable Reporting**
• Make reporting easy
• Provide multiple channels
• Remove barriers
• Ensure accessibility

**3. Encourage Participation**
• Recognize reporters
• Respond positively
• Follow up visibly
• Avoid blame

**4. Close the Loop**
• Communicate actions taken
• Explain decisions
• Thank reporters
• Show impact

**Reporting Channels:**
• Online reporting systems
• Phone hotlines
• Mobile apps
• Direct supervisor reporting
• Anonymous options
• Student reporting mechanisms

**External Networks:**
• Law enforcement relationships
• Health department connections
• Emergency management contacts
• Community partnerships
• Peer district relationships
• Professional associations

**Network Maintenance:**
• Regular communication
• Relationship building
• Information sharing
• Joint exercises
• Feedback mechanisms`,
        keyTakeaways: [
          'Every person can be a risk sensor',
          'Networks require active cultivation',
          'Multiple reporting channels increase sensing'
        ]
      },
      {
        id: 'skill8_l4',
        title: 'Signal Response',
        duration: 12,
        content: `Detecting signals is only valuable if response follows. This lesson covers effective signal response.

**Signal Response Framework:**

**1. Acknowledge**
• Confirm receipt
• Thank the reporter
• Document the signal
• Initial assessment

**2. Assess**
• Evaluate credibility
• Determine urgency
• Estimate impact
• Identify response options

**3. Act**
• Implement appropriate response
• Communicate decisions
• Deploy resources
• Monitor effects

**4. Follow Up**
• Verify effectiveness
• Update reporter
• Document outcome
• Identify lessons

**Response Levels:**

**Level 1: Monitor**
• Signal is weak or uncertain
• Continue watching
• Gather more information
• No immediate action needed

**Level 2: Investigate**
• Signal warrants attention
• Gather additional data
• Assess validity
• Determine if action needed

**Level 3: Prepare**
• Signal indicates likely risk
• Ready response resources
• Alert stakeholders
• Plan contingencies

**Level 4: Respond**
• Risk is materializing
• Implement response plan
• Deploy resources
• Communicate actively

**Common Response Failures:**
• Ignoring or dismissing signals
• Analysis paralysis
• Overreaction
• Failure to communicate
• No follow-up

**Building Response Capability:**
• Clear protocols
• Trained responders
• Available resources
• Communication systems
• Decision authority
• Practice and drills`,
        keyTakeaways: [
          'Detection without response wastes sensing capability',
          'Response should be proportional to signal strength',
          'Clear protocols enable effective response'
        ]
      }
    ]
  },
  {
    id: 'skill9',
    title: 'Clock Movements',
    description: 'Factor in velocity and momentum of change',
    duration: 40,
    icon: 'clock',
    color: '#8B5CF6',
    overview: 'Understand how the speed and momentum of change affects risk, and learn to factor timing into risk management decisions.',
    objectives: [
      'Assess the velocity of change',
      'Recognize momentum and trends',
      'Time risk responses appropriately',
      'Balance urgency with thoroughness'
    ],
    lessons: [
      {
        id: 'skill9_l1',
        title: 'Understanding Change Velocity',
        duration: 14,
        content: `Not all changes occur at the same speed. Understanding velocity is essential for appropriate response.

**Velocity Categories:**

**Sudden Events**
• Immediate impact
• Little or no warning
• Requires prepared response
• Examples: Weather emergencies, security incidents, accidents

**Rapid Changes**
• Days to weeks
• Some warning possible
• Requires quick mobilization
• Examples: Viral outbreaks, social media crises, sudden departures

**Gradual Changes**
• Months to years
• Significant lead time
• Planned response possible
• Examples: Demographic shifts, regulatory changes, facility aging

**Slow-Moving Changes**
• Years to decades
• Extended lead time
• Strategic response
• Examples: Climate change, technology evolution, social trends

**Velocity Assessment Questions:**
• How quickly is this developing?
• How much lead time do we have?
• What is the rate of acceleration/deceleration?
• Are there inflection points?
• What triggers rapid change?

**Matching Response to Velocity:**

**High Velocity**
• Pre-planned responses
• Immediate decision authority
• Practiced capabilities
• Standing resources

**Medium Velocity**
• Rapid assessment
• Quick planning
• Resource mobilization
• Active monitoring

**Low Velocity**
• Thorough analysis
• Careful planning
• Phased implementation
• Regular review

**Velocity Traps:**
• Treating slow changes as urgent (wastes resources)
• Treating fast changes as slow (misses window)
• Assuming velocity will remain constant
• Ignoring acceleration/deceleration`,
        keyTakeaways: [
          'Change velocity varies significantly',
          'Response must match velocity',
          'Velocity can change over time'
        ]
      },
      {
        id: 'skill9_l2',
        title: 'Recognizing Momentum and Trends',
        duration: 14,
        content: `Momentum and trends indicate direction and staying power of change.

**Momentum Indicators:**

**Growing Momentum**
• Accelerating change
• Expanding scope
• Increasing attention
• More participants

**Stable Momentum**
• Consistent rate
• Predictable trajectory
• Established pattern
• Sustained attention

**Declining Momentum**
• Slowing change
• Narrowing scope
• Decreasing attention
• Fewer participants

**Trend Analysis:**

**Identifying Trends**
• Historical data review
• Pattern recognition
• Statistical analysis
• Expert input

**Evaluating Trend Strength**
• Duration of trend
• Consistency of direction
• Magnitude of change
• Drivers and support

**Projecting Trends**
• Extrapolation methods
• Scenario development
• Expert forecasting
• Sensitivity analysis

**Trend Reversals:**
Watch for signs of reversal:
• Contrary indicators appearing
• Rate of change slowing
• Counter-forces emerging
• Underlying drivers changing

**K-12 Trends to Monitor:**
• Enrollment patterns
• Staffing availability
• Funding trajectories
• Technology adoption
• Safety incident trends
• Community sentiment
• Regulatory direction

**Trend Response Strategies:**
• Ride favorable trends
• Prepare for unfavorable trends
• Position for trend reversals
• Create counter-trends when possible`,
        keyTakeaways: [
          'Momentum indicates staying power',
          'Trends can continue, accelerate, or reverse',
          'Multiple indicators improve trend assessment'
        ]
      },
      {
        id: 'skill9_l3',
        title: 'Timing Risk Responses',
        duration: 12,
        content: `The timing of response can be as important as the response itself.

**Timing Considerations:**

**Too Early**
• Resources committed before needed
• Conditions may change
• Information may be incomplete
• Flexibility reduced

**Just Right**
• Adequate preparation time
• Sufficient information
• Resources available
• Conditions favorable

**Too Late**
• Limited response options
• Reduced effectiveness
• Higher costs
• Potential harm occurs

**Timing Factors:**

**External Factors**
• Regulatory deadlines
• School calendar
• Budget cycles
• Community events
• Weather patterns

**Internal Factors**
• Resource availability
• Staff capacity
• Competing priorities
• Organizational readiness
• Decision processes

**Timing Techniques:**

**Windows of Opportunity**
• Identify optimal timing windows
• Prepare for window opening
• Act decisively when window opens
• Recognize window closing

**Staged Response**
• Phase implementation
• Build capability over time
• Allow adjustment between stages
• Manage resources across phases

**Contingent Timing**
• Trigger-based response
• Predetermined conditions
• Pre-planned actions
• Automatic escalation

**Time Traps:**
• Waiting for perfect information
• Delaying difficult decisions
• Rushing due to pressure
• Missing optimal windows
• Ignoring deadlines`,
        keyTakeaways: [
          'Timing significantly affects response effectiveness',
          'Both too early and too late have costs',
          'Windows of opportunity should be identified and used'
        ]
      }
    ]
  },
  {
    id: 'skill10',
    title: 'Monitor Disruptors',
    description: 'Maintain vigilance and grasp the full picture',
    duration: 45,
    icon: 'eye',
    color: '#64748B',
    overview: 'Develop the discipline to continuously scan for disruptive forces while maintaining focus on operational priorities.',
    objectives: [
      'Identify potential disruptors',
      'Maintain strategic vigilance',
      'Balance monitoring with action',
      'Prepare for disruptive events'
    ],
    lessons: [
      {
        id: 'skill10_l1',
        title: 'Identifying Disruptors',
        duration: 15,
        content: `Disruptors are forces that can fundamentally change the risk landscape. Identifying them early provides strategic advantage.

**Categories of Disruptors:**

**Technological Disruptors**
• Artificial intelligence in education
• Virtual and remote learning
• Cybersecurity threats
• Student device ubiquity
• Data analytics capabilities

**Social Disruptors**
• Changing family structures
• Mental health crisis
• Social media influence
• Diversity and inclusion demands
• Parent activism

**Political/Regulatory Disruptors**
• School choice expansion
• Funding model changes
• Accountability shifts
• Safety mandates
• Privacy regulations

**Economic Disruptors**
• Funding volatility
• Labor market changes
• Cost escalation
• Community economic shifts
• Insurance market hardening

**Environmental Disruptors**
• Climate change effects
• Severe weather frequency
• Air quality concerns
• Facility sustainability demands
• Public health events

**Disruption Identification Methods:**

**Horizon Scanning**
• Monitor emerging developments
• Track early signals
• Watch adjacent sectors
• Follow thought leaders

**Scenario Planning**
• Develop alternative futures
• Identify key uncertainties
• Explore implications
• Test strategies

**Expert Consultation**
• Engage futurists
• Consult specialists
• Participate in networks
• Attend conferences

**Disruption Assessment:**
For each potential disruptor:
• What is the probability of occurrence?
• What is the potential magnitude?
• What is the timing uncertainty?
• What are the early indicators?
• What are the implications for us?`,
        keyTakeaways: [
          'Disruptors can fundamentally change the landscape',
          'Multiple categories of disruptors should be monitored',
          'Early identification enables strategic response'
        ]
      },
      {
        id: 'skill10_l2',
        title: 'Strategic Vigilance',
        duration: 15,
        content: `Maintaining vigilance without becoming paralyzed by possibilities requires discipline.

**The Vigilance Challenge:**
• Infinite possibilities to monitor
• Limited attention and resources
• Day-to-day demands compete
• Complacency sets in over time

**Building Vigilance Habits:**

**Personal Practices**
• Regular scanning time
• Diverse information sources
• Reflection and synthesis
• Challenge assumptions

**Team Practices**
• Dedicated discussion time
• Rotating scanning responsibilities
• External speaker engagement
• Cross-functional dialogue

**Organizational Practices**
• Strategic planning processes
• Environmental scanning function
• Early warning systems
• Scenario exercises

**Vigilance Disciplines:**

**1. Scheduled Attention**
• Calendar time for scanning
• Regular team discussions
• Periodic deep dives
• Annual strategic review

**2. Diverse Sources**
• Inside and outside education
• Multiple perspectives
• Contrarian views
• International developments

**3. Pattern Recognition**
• Connect disparate signals
• Identify themes
• Note acceleration
• Spot convergence

**4. Action Connection**
• Link insights to decisions
• Update strategies
• Adjust priorities
• Allocate resources

**Avoiding Vigilance Failures:**
• Don't dismiss weak signals
• Don't assume stability
• Don't ignore uncomfortable information
• Don't let urgency crowd out importance
• Don't rely on single sources`,
        keyTakeaways: [
          'Vigilance requires intentional habits',
          'Both individual and organizational practices matter',
          'Insights must connect to action'
        ]
      },
      {
        id: 'skill10_l3',
        title: 'Disruption Preparedness',
        duration: 15,
        content: `While specific disruptions can't always be predicted, general preparedness can be developed.

**Building Adaptive Capacity:**

**Organizational Agility**
• Flexible structures
• Quick decision processes
• Adaptable resources
• Learning culture

**Financial Resilience**
• Adequate reserves
• Diverse revenue
• Controlled costs
• Access to credit

**Operational Flexibility**
• Scalable operations
• Multiple delivery modes
• Transferable capabilities
• Modular systems

**Human Adaptability**
• Broad skills
• Change management capability
• Innovation mindset
• Psychological resilience

**Scenario-Based Preparation:**

**1. Identify Key Uncertainties**
• What major disruptions are possible?
• What are the key variables?
• What combinations are plausible?

**2. Develop Scenarios**
• Create 3-5 distinct futures
• Include optimistic and pessimistic
• Make them vivid and specific
• Challenge assumptions

**3. Test Strategies**
• How would current plans fare?
• What would need to change?
• What's robust across scenarios?
• What's vulnerable?

**4. Build Options**
• Develop contingency plans
• Create flexibility
• Position for multiple futures
• Monitor for scenario emergence

**Disruption Response Principles:**
• Accept that disruption will occur
• Respond quickly but thoughtfully
• Communicate transparently
• Learn and adapt
• Build back better`,
        keyTakeaways: [
          'General preparedness complements specific planning',
          'Adaptive capacity enables response to unknown disruptions',
          'Scenario planning builds preparation without prediction'
        ]
      }
    ]
  }
];

export default TRAINING_MODULES;
